import React, { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { type SignInOutput } from 'aws-amplify/auth';
import { login as amplifyLogin, logout as amplifyLogout, isAuthenticated, getAccessToken, getAuthUser, getSessionEmail } from '../services/auth';

export type UserRole = 'user' | 'admin' | 'event';

export interface AuthUser {
  userId: string;
  username: string;
  role: UserRole;
}

export interface AuthContextValue {
  /** The currently authenticated user, or null if not logged in. */
  user: AuthUser | null;
  /** True while the initial auth check is in progress. */
  loading: boolean;
  /** Error message from the last login attempt. */
  error: string | null;
  /** Sign in with email and password. */
  login: (email: string, password: string) => Promise<SignInOutput>;
  /** Sign out and clear session. */
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Check for existing session on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const authed = await isAuthenticated();
        if (authed && !cancelled) {
          const token = await getAccessToken();
          if (token) {
            const authUser = await getAuthUser();
            if (authUser && !cancelled) {
              setUser(authUser);
            }
          }
        }
      } catch {
        // No valid session — stay logged out
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Token refresh interval — refresh every 50 minutes (tokens last 60 min)
  useEffect(() => {
    if (!user) return;
    const interval = setInterval(async () => {
      const token = await getAccessToken();
      if (!token) {
        // Session expired — force logout
        setUser(null);
        localStorage.removeItem('authToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('tokenExpiry');
      }
    }, 50 * 60 * 1000);
    return () => clearInterval(interval);
  }, [user]);

  const login = useCallback(async (email: string, password: string): Promise<SignInOutput> => {
    setError(null);
    try {
      const result = await amplifyLogin(email, password);
      if (result.isSignedIn) {
        const token = await getAccessToken();
        if (token) {
          const authUser = await getAuthUser();
          // Verify session identity matches submitted email
          const sessionEmail = await getSessionEmail();
          if (sessionEmail && sessionEmail.toLowerCase() !== email.toLowerCase()) {
            await amplifyLogout();
            setUser(null);
            const msg = 'Session identity mismatch. Please try again.';
            setError(msg);
            throw new Error(msg);
          }
          setUser(authUser);
        }
      }
      // Return the result so caller can inspect nextStep for challenges
      return result;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Login failed. Please check your credentials.';
      setError(message);
      throw err;
    }
  }, []);

  const logout = useCallback(async () => {
    await amplifyLogout();
    setUser(null);
    setError(null);
  }, []);

  const value: AuthContextValue = { user, loading, error, login, logout };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * Hook to access the auth context. Must be used within an AuthProvider.
 */
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}
